// @flow

import React, { useContext, useState } from 'react';
import { PaddedAndRaisedSegment } from '@presentational/PaddedAndRaisedSegment';
import { PrimaryButton } from '@presentational/buttons/PrimaryButton';
import { SPStreamingProjectContext } from '@streaming-projects/sp-page/contexts/SPStreamingProjectContext';
import { DataGrid } from '@components/views/grid-utils/DataGrid';
import {
  TABLEFLOW_RESOURCES_GRID_FIELDNAMES,
  TABLEFLOW_RESOURCES_GRID_HEADERS,
} from '@streaming-projects/orgs/enums';
import { BACKEND_FIELDNAMES, FRONT_END_DISPLAY_NAMES } from '@streaming-projects/enums';
import { SPMetaDataContext } from '@streaming-projects/orgs/contexts/SPMetaDataContext';
import {
  getMappedDataValueGetter,
  getRegionName,
} from '@streaming-projects/resource-definitions/auxilary-components/MappedDataRenderer';
import { ADD_NEW_WORKLOAD_MESSAGE } from '@streaming-projects/constants';
import { ENABLED_DISABLED_FILTER_PARAMS } from '@components/views/grid-utils/constants';

import { SPTableFlowWorkloadDeleteRenderer } from './SPTableFlowWorkloadDeleteRenderer';
import { SPTableFlowWorkloadAddModal } from './SPTableFlowWorkloadAddModal';
import { SPTableFlowWorkloadNameRenderer } from './SPTableFlowWorkloadNameRenderer';
import { SPTableFlowWorkloadEnabledCellRenderer } from './SPTableFlowWorkloadEnabledCellRenderer';

export const SPTableFlowSPPageWorkloadSummaryContainer = () => {
  const { providerRegionsMap, providerNameLabelMap, tableFlowStorageTypeMap } =
    useContext(SPMetaDataContext).metaData;
  const [isTableFlowWorkloadAddModalOpen, setTableFlowWorkloadAddModalOpen] = useState(false);
  const spStreamingProjectData = useContext(SPStreamingProjectContext);
  const inputDataSource = spStreamingProjectData?.use_case_summaries?.tableflow_summaries ?? [];
  const areAnyTableFlowWorkloadsConfigured = inputDataSource.length > 0;
  const columnDefs = [
    {
      field: FRONT_END_DISPLAY_NAMES.WORKLOAD_ID,
      backEndFieldName: BACKEND_FIELDNAMES.WORKLOAD_ID,
      hide: true,
    },
    {
      field: FRONT_END_DISPLAY_NAMES.WORKLOAD_NAME,
      backEndFieldName: BACKEND_FIELDNAMES.WORKLOAD_NAME,
      cellRenderer: SPTableFlowWorkloadNameRenderer,
    },
    {
      field: FRONT_END_DISPLAY_NAMES.IS_ENABLED,
      filter: true,
      filterParams: ENABLED_DISABLED_FILTER_PARAMS,
      cellRenderer: SPTableFlowWorkloadEnabledCellRenderer,
      backEndFieldName: BACKEND_FIELDNAMES.IS_ENABLED,
      headerTooltip: FRONT_END_DISPLAY_NAMES.IS_ENABLED,
    },
    {
      field: FRONT_END_DISPLAY_NAMES.RESOURCE_ID,
      backEndFieldName: BACKEND_FIELDNAMES.RESOURCE_ID,
      hide: true,
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.NAME,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.NAME,
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.STORAGE_TYPE,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.STORAGE_TYPE,
      valueGetter: getMappedDataValueGetter(tableFlowStorageTypeMap),
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_NAME,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_NAME,
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_PROVIDER,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_PROVIDER,
      valueGetter: getMappedDataValueGetter(providerNameLabelMap),
    },
    {
      field: TABLEFLOW_RESOURCES_GRID_HEADERS.CLUSTER_RESOURCE_REGION,
      backEndFieldName: TABLEFLOW_RESOURCES_GRID_FIELDNAMES.CLUSTER_RESOURCE_REGION,
      tooltipValueGetter: (params) => getRegionName(params, providerRegionsMap),
      valueGetter: (params) => getRegionName(params, providerRegionsMap),
    },
    {
      field: '',
      filter: false,
      cellRenderer: SPTableFlowWorkloadDeleteRenderer,
      suppressSizeToFit: true,
      maxWidth: 60,
      sortable: false,
    },
  ];

  return (
    <PaddedAndRaisedSegment>
      <PrimaryButton
        onClick={() => {
          setTableFlowWorkloadAddModalOpen(true);
        }}
        size="mini"
        text={ADD_NEW_WORKLOAD_MESSAGE}
      />

      <SPTableFlowWorkloadAddModal
        isOpen={isTableFlowWorkloadAddModalOpen}
        setOpen={setTableFlowWorkloadAddModalOpen}
      />

      {areAnyTableFlowWorkloadsConfigured && (
        <DataGrid
          columnDefs={columnDefs}
          downloadAllCols={false}
          inputDataSource={inputDataSource}
          label="Tableflow Workloads Summary"
          sizeColumnsToFitInitially={true}
          tooltipShowDelay={0}
        />
      )}
    </PaddedAndRaisedSegment>
  );
};
